import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SectionHeader } from "../../../components/GlobalComponents/SectionHeader/SectionHeader";
import Icon from "../../../components/Icon/Icon";
import {
  useCustomDashboardList,
  useCustomDashboardMutations,
} from "./hooks/useCustomDashboards";
import { emptyContent } from "./utils/dashboardContent";

const TYPE_LABEL = { one_page: "One Page", slides: "Slides" };

const CreateDialog = ({ open, onClose, onCreate, creating }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("one_page");

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate({ name: trimmed, type });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // MUI zeroes DialogContent's top padding when it directly follows a
      // DialogTitle (adjacent-sibling rule, higher specificity than a plain
      // sx), which clips the outlined label's focused/notch state. Restore a
      // little top padding — scoped to this dialog only.
      sx={{ "& .MuiDialogTitle-root + .MuiDialogContent-root": { pt: "10px" } }}
    >
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 600 }}>Nuevo BI Panel</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          autoFocus
          size="small"
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          fullWidth
        />
        <TextField
          select
          size="small"
          label="Formato"
          value={type}
          onChange={(e) => setType(e.target.value)}
          fullWidth
        >
          <MenuItem value="one_page">One Page</MenuItem>
          <MenuItem value="slides">Slides</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={submit}
          disabled={!name.trim() || creating}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CustomDashboardList = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCustomDashboardList();
  const { create, remove } = useCustomDashboardMutations();
  const [dialogOpen, setDialogOpen] = useState(false);

  const dashboards = Array.isArray(data) ? data : [];

  const handleCreate = ({ name, type }) => {
    create.mutate(
      { name, type, content: emptyContent(type) },
      {
        onSuccess: (created) => {
          setDialogOpen(false);
          if (created?.id) navigate(`/analytics/custom-dashboards/${created.id}/edit`);
        },
      }
    );
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (window.confirm("¿Eliminar este BI Panel?")) remove.mutate(id);
  };

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="BI Panel"
        subtitle="Dashboards con resultados congelados — no vuelven a consultar datos."
        icon={<Icon name="bold_element_2" size={48} />}
      />

      <Box sx={{ mt: 2 }}>
        {isLoading ? (
          <Typography color="text.secondary">Cargando…</Typography>
        ) : isError ? (
          <Typography color="error">No se pudieron cargar los dashboards.</Typography>
        ) : (
          <Grid container spacing={2}>
            {/* "New" card — dashed placeholder that opens the create dialog */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card
                className="rounded"
                variant="outlined"
                onClick={() => setDialogOpen(true)}
                sx={{
                  height: "100%",
                  minHeight: 132,
                  bgcolor: "transparent",
                  border: "2px dashed",
                  borderColor: "grey.400",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  transition: "border-color .15s, color .15s",
                  "& svg, & svg *": { fill: "currentColor" },
                  "&:hover": { borderColor: "primary.main", color: "primary.main" },
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Icon name="bold_add_square" size={40} />
                  <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                    Nuevo
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {dashboards.map((d) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={d.id}>
                <Card className="dyTheme1 dyBorder1 rounded" sx={{ height: "100%" }}>
                  <CardActionArea
                    onClick={() => navigate(`/analytics/custom-dashboards/${d.id}`)}
                    sx={{ height: "100%" }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                        <Chip size="small" label={TYPE_LABEL[d.type] || d.type} />
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            component="span"
                            onClick={(e) => handleDelete(e, d.id)}
                          >
                            <Icon name="bold_trash" size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 1.5, fontWeight: 600, fontSize: "0.9rem" }}
                        noWrap
                      >
                        {d.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.updated_at
                          ? new Date(d.updated_at).toLocaleDateString()
                          : ""}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <CreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreate={handleCreate}
        creating={create.isPending}
      />
    </Box>
  );
};

export default CustomDashboardList;
